import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  #abortController = null;
  #cachedContent = new Map();

  get productPageUrl() {
    const cartDrawer = this.closest('cart-drawer, [data-cart-upsell-carousel]');
    if (cartDrawer) {
      return this.#getProductUrlFromCartUpsell();
    }
    
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  #getProductUrlFromCartUpsell() {
    const productCard = this.closest('.cart-upsell__product-card');
    if (!productCard) return '';
    
    const productTitle = productCard.querySelector('.cart-upsell__product-title');
    if (productTitle && productTitle.href) {
      const url = new URL(productTitle.href);
      const variantId = this.#getSelectedVariantId();
      if (variantId) {
        url.searchParams.set('variant', variantId);
      }
      return url.toString();
    }
    
    const productLink = productCard.querySelector('.cart-upsell__product-link');
    if (productLink && productLink.href) {
      const url = new URL(productLink.href);
      const variantId = this.#getSelectedVariantId();
      if (variantId) {
        url.searchParams.set('variant', variantId);
      }
      return url.toString();
    }
    
    return '';
  }

  #getSelectedVariantId() {
    const form = this.querySelector('form');
    if (form) {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput && variantInput.value) {
        return variantInput.value;
      }
    }
    
    const cartDrawer = this.closest('cart-drawer, [data-cart-upsell-carousel]');
    if (cartDrawer) {
      const productCard = this.closest('.cart-upsell__product-card');
      if (productCard && productCard.dataset.defaultVariantId) {
        return productCard.dataset.defaultVariantId;
      }
    }
    
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    if (!this.dataset.productTitle) {
      const productCard = this.closest('.cart-upsell__product-card');
      if (productCard) {
        const productTitle = productCard.querySelector('.cart-upsell__product-title');
        if (productTitle) {
          this.dataset.productTitle = productTitle.textContent || '';
        }
      }
    }

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    
    if (!currentUrl) return;

    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        } else {
          const productMain = html.querySelector('.product, .product-template, main');
          if (productMain) {
            productGrid = /** @type {Element} */ (productMain.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }
    }

    if (productGrid) {
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
    } else {
      this.#showErrorMessage();
      return;
    }

    this.#openQuickAddModal();
  };

  #showErrorMessage() {
    const button = this.querySelector('.quick-add__button-cart');
    if (!button) return;
    
    const buttonText = button.querySelector('.add-to-cart-text__content');
    if (!buttonText) return;
    
    const originalText = buttonText.textContent;
    buttonText.textContent = 'Error loading product';
    buttonText.style.color = '#f44336';
    
    setTimeout(() => {
      buttonText.textContent = originalText;
      buttonText.style.color = '';
    }, 2000);
  }

  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) {
        const productMain = productGrid.querySelector('.product__info-wrapper, .product-info, .product-form');
        if (productMain) {
          const productFormComponent = productGrid.querySelector('product-form-component, form[data-product-form]');
          const variantPicker = productGrid.querySelector('variant-picker, .product-form__input');
          const productPrice = productGrid.querySelector('product-price, .price');
          const productTitle = document.createElement('a');
          
          const titleElement = productGrid.querySelector('.product__title, h1, [data-product-title]');
          productTitle.textContent = titleElement?.textContent || this.dataset.productTitle || '';
          productTitle.href = this.productPageUrl;
          productTitle.style.cssText = 'font-size: 18px; font-weight: bold; margin-bottom: 10px; display: block;';

          if (productFormComponent || variantPicker || productPrice) {
            const productHeader = document.createElement('div');
            productHeader.classList.add('product-header');
            productHeader.style.cssText = 'padding: 15px;';

            if (productTitle.textContent) {
              productHeader.appendChild(productTitle);
            }
            
            if (productPrice) {
              productHeader.appendChild(productPrice.cloneNode(true));
            }
            
            const newContainer = document.createElement('div');
            newContainer.classList.add('quick-add-mobile-container');
            
            newContainer.appendChild(productHeader);
            
            if (variantPicker) {
              newContainer.appendChild(variantPicker.cloneNode(true));
            }
            
            if (productFormComponent) {
              newContainer.appendChild(productFormComponent.cloneNode(true));
            }
            
            while (modalContent.firstChild) {
              modalContent.removeChild(modalContent.firstChild);
            }
            modalContent.appendChild(newContainer);
            
            morph(newContainer, newContainer);
            return;
          }
        }
      } else {
        const productFormComponent = productGrid.querySelector('product-form-component');
        const variantPicker = productGrid.querySelector('variant-picker');
        const productPrice = productGrid.querySelector('product-price');
        const productTitle = document.createElement('a');
        productTitle.textContent = this.dataset.productTitle || '';
        productTitle.href = this.productPageUrl;

        if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

        const productHeader = document.createElement('div');
        productHeader.classList.add('product-header');

        productHeader.appendChild(productTitle);
        productHeader.appendChild(productPrice);
        productGrid.appendChild(productHeader);
        productGrid.appendChild(variantPicker);
        productGrid.appendChild(productFormComponent);
        productDetails.remove();
      }
    }

    morph(modalContent, productGrid);
    this.#syncVariantSelection(modalContent);
  }

  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id], input[type="radio"][name="id"], select[name="id"]');
    
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && 
          (input.dataset.variantId === selectedVariantId || input.value === selectedVariantId) && 
          !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      } else if (input instanceof HTMLSelectElement && input.value === selectedVariantId) {
        input.value = selectedVariantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}